import { SurveyPayload } from './../../../../../shared/interfaces/survey-meta-data.interface';
import { Qualification } from './../../../../../shared/interfaces/qualification.interface';
import { Flexible } from './../../../../../shared/interfaces/create-surveys.interface';
import { SelectedQuota } from './../../../../../shared/interfaces/quota.interface';
import { QuotaModalService } from '../../../../quota-modal/quota-modal.service';
import {
  BuyerSurveyResponse,
  Quotas,
  Qualification as SurveyQualification,
} from './../../../../../shared/interfaces/survey.interface';
import { omit } from 'lodash';
import { SurveyPayloadService } from '../../../../survey-payload.service';

export class ApplyCensusToMcSurveys {
  constructor(
    private _completes: number,
    private _qualification: Qualification,
    private _quotaModalService: QuotaModalService
  ) {}

  applyCensus() {
    if (this._qualification.is_range) {
      return this.applyRangeCensus();
    }
    return this.applyMultiselectCensus();
  }

  public applyMultiselectCensus(): SelectedQuota[] | void {
    if (this._quotaModalService.inValidComplete(this._completes)) {
      return;
    }
    const distributedQuotas = this._quotaModalService.distributeQuotas(
      this._qualification,
      this._completes,
      this._qualification.flexible
    );
    const mappedQuotas = this._quotaModalService.adjustQuotaQuantites(
      distributedQuotas,
      this._completes,
      this._qualification.flexible,
      true
    );

    if (mappedQuotas.length) {
      const quotas = mappedQuotas;
      this._qualification.data = this._qualification.data.map((item) => {
        item.selected = true;
        return item;
      });
      return quotas;
    }
    return [];
  }

  public applyRangeCensus(): SelectedQuota[] | void {
    if (this._quotaModalService.inValidComplete(this._completes)) {
      return;
    }

    const distributedQuotas = this._distributeQuotas(
      this._qualification,
      this._qualification.flexible!,
      this._completes
    );
    const mappedQuotas = this._adjustQuotaQuantites(
      distributedQuotas,
      this._completes,
      this._qualification.flexible!
    );

    if (mappedQuotas.length) {
      const [min, max] = mappedQuotas.reduce(
        (acc, cur, index) => {
          if (index) {
            return [
              Math.min(Number(cur.min || 0), acc[0]),
              Math.max(Number(cur.max || 0), acc[1]),
            ];
          }
          return acc;
        },
        [Number.POSITIVE_INFINITY, Number.NEGATIVE_INFINITY]
      );
      this._qualification.data[0].minValue = min;
      this._qualification.data[0].maxValue = max;
      return mappedQuotas;
    }
    return [];
  }

  private _distributeQuotas(
    qualification: Qualification,
    flexible: Flexible,
    completes: number
  ) {
    if (qualification.census?.data?.length) {
      const censusData = [...qualification.census.data];
      const emptyRow = {
        min: null,
        max: null,
        rep_value: 0,
      };
      censusData.unshift(emptyRow);

      return censusData.map((census) => {
        let [minimum, maximum] = this._quotaModalService.getFlexibleValue(
          census.rep_value,
          flexible
        );
        return {
          min: census.min,
          max: census.max,
          number: Math.round((census.rep_value * Number(completes)) / 100),
          percentage: Number(census.rep_value),
          minimum: Number(minimum),
          maximum: Number(maximum),
        };
      });
    }
    return [];
  }

  private _adjustQuotaQuantites(
    distributedQuotas: SelectedQuota[],
    completes: number,
    flexible: Flexible
  ) {
    if (distributedQuotas.length) {
      const isCensusApplied = true;
      return distributedQuotas.map((quota, index) => {
        const number = this._quotaModalService.getNumber(
          completes,
          distributedQuotas,
          quota.percentage,
          index,
          isCensusApplied
        );
        let [minimum, maximum] = this._quotaModalService.getFlexibleValue(
          number,
          flexible
        );
        let obj = {
          min: quota.min,
          max: quota.max,
          number: number,
          percentage: quota.percentage,
          minimum: minimum,
          maximum: maximum,
        };
        distributedQuotas[index] = obj;
        return obj;
      });
    }

    return [];
  }
}
//
export class McQuotaQualificationActions {
  constructor(
    private _allMcChildSurveys: { [surveyId: number]: BuyerSurveyResponse },
    private _appliedQualification: Qualification,
    private _surveyPayloadService?: SurveyPayloadService
  ) {}

  mapPayloadForMc(surveyId: number) {
    // only use for traditional for now
    const payload = this._allMcChildSurveys[
      surveyId
    ] as unknown as SurveyPayload;
    const mappedPayload = this._surveyPayloadService!.removeEmpty(
      omit(omit(payload, 'country_code'), 'ps_survey_status')
    );
    return mappedPayload;
  }

  updateQuotaQualificationInMcSurveys(
    surveyId: number,
    qualification: Qualification,
    finalQuotas: Quotas[],
    mappedQualification: SurveyQualification[]
  ) {
    // filter previous qualification or quotas if any
    this._allMcChildSurveys[surveyId].quotas = this._allMcChildSurveys[
      surveyId
    ]?.quotas.filter(
      (q) =>
        !q.criteria.some(
          (item) => item.qualification_code === qualification.qualification_code
        )
    );
    this._allMcChildSurveys[surveyId].qualifications = this._allMcChildSurveys[
      surveyId
    ].qualifications?.filter(
      (q) => q.qualification_code !== qualification.qualification_code
    );

    this._allMcChildSurveys[surveyId]?.quotas?.push(...finalQuotas);
    this._allMcChildSurveys[surveyId].qualifications?.push(
      ...mappedQualification
    );

    return this._allMcChildSurveys;
  }
}
